import React, { useEffect, useState } from "react";
import ReusableTable from "../../../components/common/table";
import { Button, Space, Tag } from "antd";
import { ReactComponent as AddSvg } from "../../../assets/images/add.svg";
import { useNavigate, useSearchParams } from "react-router";
import ConfirmModal from "../../../components/common/alert-popup";
import { ReactComponent as DeleteSvg } from "../../../assets/images/action/delete.svg";
import { ReactComponent as DisableSvg } from "../../../assets/images/action/Shield Cross.svg";
import { ReactComponent as EnableSvg } from "../../../assets/images/action/Shield Check.svg";

import CommonFilter from "../../../components/common/filter";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getAdvData } from "../../../redux/action/advertisement/advertisingAction";
import { useDebounce } from "../../../customHooks";
import {
  deleteUser,
  updateStatus,
} from "../../../redux/action/user-management/userManagementAction";
import { ApiUrl } from "../../../ApiHelper/ApiUrl";
import { ReactComponent as EditSvg } from "../../../assets/images/action/edit.svg";

const Advertising = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserStatus, setSelectedUserStatus] = useState(null);

  const [modalConfig, setModalConfig] = useState({
    open: false,
    actionType: "",
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialParams = () => {
    const page = parseInt(searchParams.get("page")) || 1;
    const size = parseInt(searchParams.get("size")) || 10;
    const search_key = searchParams.get("search_key") || "all";
    const search_value = searchParams.get("search_value") || "";
    const status = searchParams.get("status");
    const position = searchParams.get("position");

    return {
      page,
      size,
      search_key,
      search_value,
      status: status !== null ? Number(status) : undefined,
      position: position !== null ? Number(position) : undefined,
    };
  };

  const [tableParams, setTableParams] = useState(getInitialParams());

  useEffect(() => {
    const params = {
      page: tableParams.page,
      size: tableParams.size,
      search_key: tableParams.search_key,
      search_value: tableParams.search_value,
    };
    if (tableParams.status !== undefined) params.status = tableParams.status;
    if (tableParams.position !== undefined)
      params.position = tableParams.position;

    setSearchParams(params);
  }, [tableParams, setSearchParams]);

  const showModal = (actionType, userId = null, status = null) => {
    setSelectedUserId(userId);
    setSelectedUserStatus(status);
    setModalConfig({ open: true, actionType });
  };

  const closeModal = () => {
    setModalConfig({ open: false, actionType: "" });
  };

  const columns = [
    {
      title: "SR. NO.",
      dataIndex: "id",
      key: "id",
      render: (text, record, index) =>
        (tableParams.page - 1) * tableParams.size + index + 1,
    },
    {
      title: "Title",
      dataIndex: "title_en",
      key: "title_en",
      type: "search",
    },
    {
      title: "Media",
      dataIndex: "url_en",
      key: "url_en",
      // type: "search",
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      render: (status) => (status === 1 ? "Top" : "Bottom"),
      type: "select",
      options: [
        { label: "Top", value: 1 },
        { label: "Bottom", value: 0 },
      ],
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      key: "created_by",
      render: (status) => (status === 1 ? "Admin" : "Tenant"),
    },
    {
      title: "Date Added",
      dataIndex: "created_at",
      key: "created_at",
      render: (created_at) =>
        created_at ? dayjs(created_at).format("MM-DD-YYYY") : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      type: "select",
      render: (status) => {
        const isActive = status === 1;
        return (
          <Tag
            color={isActive ? "#E6F4EA" : "#FCE8E8"}
            style={{
              color: isActive ? "#22AD5C" : "#FF5D5D",
            }}
            className="custom-tag"
          >
            {isActive ? "Active" : "Inactive"}
          </Tag>
        );
      },
      options: [
        { label: "Active", value: 1 },
        { label: "Inactive", value: 0 },
      ],
    },
  ];

  const actionItems = (user) => [
    {
      key: "edit",
      label: (
        <>
          <EditSvg style={{ marginRight: 8, color: "red" }} />
          Edit Advertisement
        </>
      ),
      onClick: () => navigate(`edit-advertisement/${user?.id}`),
    },
    {
      key: "disable",
      label: (
        <>
          {user?.status === 1 ? (
            <>
              <DisableSvg style={{ marginRight: 8, color: "red" }} />
              Deactivate Advertisement
            </>
          ) : (
            <>
              <EnableSvg style={{ marginRight: 8 }} />
              Activate Advertisement
            </>
          )}
        </>
      ),
      onClick:
        user?.status === 1
          ? () => showModal("deactivate", user.id, user.status)
          : () => showModal("activate", user.id, user.status),
    },
    {
      key: "delete",
      label: (
        <>
          <DeleteSvg style={{ marginRight: 8, color: "red" }} />
          Delete Advertisement
        </>
      ),
      onClick: (user) => showModal("delete", user.id),
    },
  ];
  const debouncedSearchValue = useDebounce(tableParams.search_value, 500);

  useEffect(() => {
    dispatch(getAdvData(tableParams));
  }, [
    debouncedSearchValue,
    tableParams.status,
    tableParams.position,
    tableParams.page,
    tableParams.size,
  ]);

  const handleFilterChange = (key, value, reset = false) => {
    setTableParams((prev) => {
      if (reset) {
        return {
          ...prev,
          search_key: "",
          search_value: "",
          status: undefined,
          position: undefined,
        };
      }
      if (key === "status" || key === "position") {
        return {
          ...prev,
          [key]: value,
          search_key: "",
          search_value: "",
        };
      }
      return {
        ...prev,
        search_key: key,
        search_value: value,
        status: undefined,
        position: undefined,
      };
    });
  };

  const datas = useSelector((store) => store.getAdvList?.data);
  const { isLoading } = useSelector((store) => store.getAdvList);

  const paginationConfig = {
    pageSizeOptions: ["10", "20", "50"],
    showSizeChanger: true,
    showQuickJumper: true,
    total: datas?.count ?? 0,
    current: tableParams.page,
    pageSize: tableParams.size,
    onChange: (page, pageSize) => {
      // setCurrentPage(page);
      setTableParams((prev) => ({ ...prev, page, size: pageSize }));
    },
  };

  const handleDelete = () => {
    if (selectedUserId) {
      dispatch(
        deleteUser(
          {
            url: ApiUrl.CLIENT_ADV_DATA_DELETE,
            id: `advertisement_id=${selectedUserId}`,
          },
          (res) => {
            if (res?.data?.code === 200) {
              dispatch(getAdvData());
              closeModal();
            }
          }
        )
      );
    }
  };
  const handleUpdateStatus = (currentStatus) => {
    const newStatus = currentStatus === 0 ? 1 : 0;
    if (selectedUserId) {
      dispatch(
        updateStatus(
          {
            url: ApiUrl.CLIENT_ADV_DATA_GET,
            id: `advertisement_id=${selectedUserId}`,
            status: `status=${newStatus}`,
          },
          (res) => {
            if (res?.data?.code === 200) {
              dispatch(getAdvData());
              closeModal();
            }
          }
        )
      );
    }
  };
  const handleTableChange = (pagination) => {
    setTableParams((prev) => ({
      ...prev,
      page: pagination.current,
      size: pagination.pageSize,
    }));
  };
  return (
    <div>
      <div className="addBx">
        <div className="filterBx">
          <Space>
            <CommonFilter
              columns={columns}
              onFilterChange={handleFilterChange}
            />
          </Space>
        </div>
        <Button
          type="primary"
          icon={<AddSvg />}
          onClick={() => navigate("add-advertisement")}
        >
          Add Advertisement
        </Button>
      </div>
      <div className="tableHeading">List of Advertisement Added</div>
      <ReusableTable
        columns={columns}
        data={datas?.rows ?? []}
        paginationConfig={{
          ...paginationConfig,
          ...tableParams.pagination,
        }}
        handleTableChange={handleTableChange}
        actionItems={actionItems}
        loading={isLoading}
      />
      <ConfirmModal
        titlePrefix="Advertisement"
        messagePrefix="Advertisement"
        open={modalConfig.open}
        onClose={closeModal}
        onConfirm={
          modalConfig.actionType === "delete"
            ? handleDelete
            : () => handleUpdateStatus(selectedUserStatus)
        }
        actionType={modalConfig.actionType}
      />
    </div>
  );
};

export default Advertising;
